import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const previousIndex = this.#current;
    this.#current = current;

    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    let prevRelative = previousIndex % slides.length;
    if (prevRelative < 0) prevRelative += slides.length;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });

    const gsap = /** @type {any} */ (window).gsap;
    if (!gsap) return;

    const incoming = slides[relativeIndex];
    const outgoing = slides[prevRelative];
    if (!incoming || !outgoing || incoming === outgoing) return;

    // Direction: next (going forward) → outgoing slides LEFT, incoming enters from RIGHT.
    // Previous (going back) → outgoing slides RIGHT, incoming enters from LEFT.
    const goingForward = current > previousIndex;
    const outgoingX = goingForward ? -20 : 20;
    const incomingFromX = goingForward ? 20 : -20;

    gsap.killTweensOf([incoming, outgoing]);

    gsap.set([incoming, outgoing], {
      visibility: 'visible',
      transition: 'none',
      willChange: 'transform, opacity'
    });

    gsap.to(outgoing, {
      x: outgoingX,
      opacity: 0,
      duration: 0.5,
      ease: 'power3.out',
      onComplete: () => {
        outgoing.style.removeProperty('visibility');
        outgoing.style.removeProperty('transition');
        outgoing.style.removeProperty('will-change');
      }
    });

    gsap.fromTo(
      incoming,
      { x: incomingFromX, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.5,
        ease: 'power3.out',
        onComplete: () => {
          incoming.style.removeProperty('transition');
          incoming.style.removeProperty('will-change');
        }
      }
    );
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
